import { VarResult } from '@uefacom-newdev/liveblog-integration-react';
import type { Translated } from '@uefadigital/panenka';
import { PkIdentifier, PkMatchFactEvent } from '@uefadigital/panenka-react';
import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';

import { getPlayerUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import { useTranslateFn } from '@shared/Translate';
import type { HomeAway, Player, PlayerEvent, RoundPhase } from '@shared-types/match.types';
import { EventType, MatchPhase } from '@shared-types/match.types';
import { IS_DEBUG } from '@utilities/browser';

import * as classes from './styles.module.scss';

const phaseName = {
  [MatchPhase.FIRST_HALF]: 'half_time',
  [MatchPhase.SECOND_HALF]: 'timeline_full_time',
  [MatchPhase.EXTRA_TIME_FIRST_HALF]: 'end_1st_extra',
  [MatchPhase.EXTRA_TIME_SECOND_HALF]: 'end_2nd_extra',
  [MatchPhase.PENALTY]: 'end_penalty_shootout',
} as const;

const PlayerLink = ({
  player,
  enablePlayerLink,
  slot,
  phase,
  competitionId,
  children,
}: {
  player: Translated<Player>;
  enablePlayerLink: boolean;
  slot: string;
  phase: RoundPhase;
  competitionId: string;
  children: ReactNode;
}) =>
  enablePlayerLink ? (
    <a
      className='pk-text--text-01'
      href={getPlayerUrl(player.id, competitionId, phase)}
      title={player.name}
      slot={slot}
    >
      {children}
    </a>
  ) : (
    <span title={player.name} slot={slot}>
      {children}
    </span>
  );

const Event = ({
  event,
  team,
  isFemaleCompetition,
  isFutsalMatch,
  enablePlayerLink,
  competitionId,
  phase,
}: {
  event: PlayerEvent;
  team: HomeAway;
  isFemaleCompetition: boolean;
  isFutsalMatch: boolean;
  enablePlayerLink: boolean;
  competitionId: string;
  phase: RoundPhase;
}) => {
  const t = useTranslateFn();

  const varPrimaryLabel = useMemo(
    () =>
      event.varInfo &&
      `var-descr-${event.varInfo.status}-${
        event.varInfo.result === VarResult.NONE ? event.varInfo.incident : event.varInfo.result
      }`,
    [event.varInfo],
  );

  const tagSuffix = isFemaleCompetition ? '_f' : '';
  const varSecondaryLabel = useMemo(
    () =>
      event.varInfo &&
      `var-reason-${event.varInfo.incident}-${event.varInfo.status}-${event.varInfo.reason}${tagSuffix}`,
    [event.varInfo, tagSuffix],
  );

  const primaryPlayer = useMemo(
    () =>
      hydrateWithTranslations(((event.primaryActor?.person as any)?.person as Player) ?? event.primaryActor?.person),
    [event.primaryActor?.person],
  );
  const secondaryPlayer = useMemo(
    () =>
      hydrateWithTranslations(
        ((event.secondaryActor?.person as any)?.person as Player) ?? event.secondaryActor?.person,
      ),
    [event.secondaryActor?.person],
  );

  if (event.type === EventType.INJURY_TIME && !event.injuryTimeMinutesAdded) return null;

  return (
    <PkMatchFactEvent
      key={event.id}
      type={event.type}
      subType={event.subType}
      phase={event.phase}
      additionalTime={event.injuryTimeMinutesAdded}
      team={team}
      title={IS_DEBUG ? JSON.stringify(event) : ''}
      className={classes.event}
    >
      {(!!event.time?.minute || !!event.time?.second) && (
        <span className='pk-text--text-02 pk-font-size--xs  pk-font-size-sm--s' slot='time'>
          {`${event.time.minute}'`}
          {isFutsalMatch && !!event.time.second && `${event.time.second}''`}
          {!!event.time.injuryMinute && (
            <span className='pk-font-size--xs2 pk-font-size-sm--xs'>+{event.time.injuryMinute}</span>
          )}
        </span>
      )}
      {primaryPlayer && (
        <PkIdentifier>
          {event.type === EventType.SUBSTITUTION ? (
            <>
              <PlayerLink
                player={secondaryPlayer}
                enablePlayerLink={enablePlayerLink}
                competitionId={competitionId}
                phase={phase}
                slot='primary'
              >
                {secondaryPlayer.shortName}
              </PlayerLink>
              <PlayerLink
                player={primaryPlayer}
                enablePlayerLink={enablePlayerLink}
                competitionId={competitionId}
                phase={phase}
                slot='secondary'
              >
                {t('substitution_for', { replace: primaryPlayer.shortName })}
              </PlayerLink>
            </>
          ) : (
            <PlayerLink
              player={primaryPlayer}
              enablePlayerLink={enablePlayerLink}
              competitionId={competitionId}
              phase={phase}
              slot='primary'
            >
              {primaryPlayer.shortName}
            </PlayerLink>
          )}
        </PkIdentifier>
      )}
      {event.type === EventType.END_PHASE && phaseName[event.phase] && (
        <span className='pk-font-size--xs pk-font-size-sm--s pk-text--text-02'>{t(phaseName[event.phase])}</span>
      )}

      {event.type === EventType.VAR && (
        <PkIdentifier>
          <span slot='primary' title={t(varPrimaryLabel)}>
            {t(varPrimaryLabel)}
          </span>
          {!!varSecondaryLabel && t(varSecondaryLabel) && (
            <span slot='secondary' title={t(varSecondaryLabel)}>
              {t(varSecondaryLabel)}
            </span>
          )}
        </PkIdentifier>
      )}
    </PkMatchFactEvent>
  );
};

export default memo(Event);
